import type { Author, SanitySeo } from "./shared";

export type MetaData = {
	title?: string;
	description?: string;
	author?: Author[] | null;
	image?: string;
	imageAlt?: string | null;
	imageWidth?: number;
	imageHeight?: number;
	keywords?: string[];
	site?: string;
	type?: string;
	canonical?: string;
	datePublished?: string;
	/** When true, emits `<meta name="robots" content="noindex">`. Combined
	 *  with `noFollow` for `noindex,nofollow`. */
	noIndex?: boolean;
	/** When true, emits `<meta name="robots" content="nofollow">`. */
	noFollow?: boolean;
	/** ISO date the editor last reviewed this page. The single source for
	 *  "last modified": emitted as `og:updated_time`, `article:modified_time`
	 *  and schema.org `dateModified`. Deliberately editor-curated — Sanity's
	 *  `_updatedAt` moves on every typo fix, which would claim a freshness
	 *  the content doesn't have. Unset means we claim no revision at all. */
	lastReviewed?: string;
	// Product-specific fields for Google rich results
	category?: string;
	price?: number;
	priceValidUntil?: string;
	campaign?: {
		name: string;
		price: number;
		validFrom?: string;
		validThrough?: string;
	};
};

/**
 * Maps an editor-authored Sanity `seo` object onto the subset of MetaData props
 * it can fill. Call sites spread the result and layer their own title/description
 * fallbacks on top, e.g. `{ title, description, canonical, ...seoToMetaData(seo) }`.
 *
 * Unset fields are omitted rather than returned as `undefined`, so the spread
 * cannot blank a value the call site already resolved — notably `image`, which
 * requires the caller to have built `seo.image.src` first.
 */
export const seoToMetaData = (seo?: SanitySeo | null): Partial<MetaData> =>
	Object.fromEntries(
		Object.entries({
			image: seo?.image?.src,
			imageAlt: seo?.image?.alt,
			imageWidth: seo?.image?.width,
			imageHeight: seo?.image?.height,
			noIndex: seo?.noIndex,
			noFollow: seo?.noFollow,
			lastReviewed: seo?.lastReviewed,
		}).filter(([, value]) => value !== undefined)
	) as Partial<MetaData>;

export type DatasetVariable = {
	name: string;
	unitText?: string;
	value?: string | number;
};

export type DatasetSchema = {
	name: string;
	description?: string;
	license?: string;
	temporalCoverage?: string;
	spatialCoverage?: string;
	variableMeasured?: DatasetVariable[];
};

export type FaqItem = {
	question: string;
	answer: string;
};

export type ListItem = {
	"@type": string;
	name: string;
	url: string;
	[key: string]: unknown;
};
